import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import ProtectedImage from '../components/ProtectedImage';

const ImageContainer = styled.div`
  width: 100%;
  margin: 0 auto;
  overflow: hidden;

  img {
    display: block;
    width: 100%;
    height: auto;
    max-width: 100%;
    object-fit: cover;
  }

  /* The plugin wraps the image in a div.magnify and appends its glass as
     div.magnify-lens inside it, at z-index 99 and hit-testable. Neither that
     wrapper nor a typical host container opens a stacking context, so the
     lens ends up competing directly with anything a caller positions over
     the photo, and a 200px circle glued to the cursor outranks a 36px
     control every time: it paints over the control and swallows the click
     before the control ever sees it.

     Every mouse handler the plugin binds lives on div.magnify, never on the
     lens itself, so taking the lens out of hit testing costs the zoom
     nothing and hands overlaid controls their clicks back. Callers still
     need a z-index above 99 if the control is to stay visible while the
     glass passes under it.

     NOTE: the rule this replaces targeted .magnify-glass, which is not a
     class this plugin version emits, so it never matched anything. 200px is
     the plugin's own default; it is written here on the real selector so the
     size is a decision rather than an inherited accident. */
  .magnify-lens {
    width: 200px;
    height: 200px;
    pointer-events: none;
  }
`;

const MagnifiableImage = ({ src, alt, zoomLevel = 1.2, style, className }) => {
    const imgRef = useRef(null);

    useEffect(() => {
        const loadScript = (src) => {
            return new Promise((resolve, reject) => {
                const script = document.createElement('script');
                script.src = src;
                script.onload = resolve;
                script.onerror = reject;
                document.body.appendChild(script);
            });
        };

        const loadStylesheet = (href) => {
            return new Promise((resolve, reject) => {
                const link = document.createElement('link');
                link.rel = 'stylesheet';
                link.href = href;
                link.onload = resolve;
                link.onerror = reject;
                document.head.appendChild(link);
            });
        };

        const initializeMagnify = async () => {
            try {
                await loadScript('https://code.jquery.com/jquery-3.6.0.min.js');
                await loadStylesheet('https://cdn.jsdelivr.net/npm/magnify@2.3.3/dist/css/magnify.css');
                await loadScript('https://cdn.jsdelivr.net/npm/magnify@2.3.3/dist/js/jquery.magnify.min.js');

                if (imgRef.current && window.jQuery && window.jQuery(imgRef.current).magnify) {
                    window.jQuery(imgRef.current).magnify({
                        speed: 100,
                        src: src,
                        zoom: zoomLevel,
                        onload: function () {
                            console.log('Magnify initialized with zoom level:', zoomLevel);
                        }
                    });
                } else {
                    console.error('Magnify plugin or jQuery not available');
                }
            } catch (error) {
                console.error('Failed to load magnify:', error);
            }
        };

        initializeMagnify();

        return () => {
            // Clean up the added elements when component unmounts
            const jqueryScript = document.querySelector('script[src$="jquery-3.6.0.min.js"]');
            const magnifyCSS = document.querySelector('link[href$="magnify.css"]');
            const magnifyJS = document.querySelector('script[src$="jquery.magnify.min.js"]');
            if (jqueryScript) jqueryScript.remove();
            if (magnifyCSS) magnifyCSS.remove();
            if (magnifyJS) magnifyJS.remove();
        };
    }, [src, zoomLevel]);

    return (
        <ImageContainer style={style} className={className}>
            <ProtectedImage ref={imgRef} src={src} alt={alt} data-magnify-src={src} />
        </ImageContainer>
    );
};

export default MagnifiableImage;
